import React from 'react';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import styled from '@emotion/styled';
import Modal from '@influitive/secret-garden/lib/modal';
import ModalArea from '@influitive/secret-garden/lib/modal/modal-area';
import ModalHeader from '@influitive/secret-garden/lib/modal/modal-header';
import ModalTitle from '@influitive/secret-garden/lib/modal/modal-title';
import Input from '@influitive/secret-garden/lib/form-inputs/input';
import Flex from '@influitive/secret-garden/lib/flex';
import Avatar from '@influitive/secret-garden/lib/avatar';
import { editProfile, updateProfile } from '../../common/api';
import Button from '@influitive/secret-garden/lib/button';
import TypeAhead from '@influitive/secret-garden/lib/form-inputs/type-ahead';
import Preloader from './preloader';
import { themeGet } from 'styled-system';
import { withNamespaces } from 'react-i18next';

const BottomLinksContainer = styled(Flex)`
border-bottom: 1px solid ${themeGet('colors.neutral.4')};
`;

const ProfileFileInput = styled('input')`
  padding-top: ${themeGet('spacing.3')}px;
`;

const InputWrapper = styled('div')`
  margin: ${themeGet('spacing.2')}px;
  flex: 0 0 calc(50% - 2*${themeGet('spacing.2')}px);
`;

const TypeaheadInputWrapper = styled(InputWrapper)`
  margin-top: calc(${themeGet('spacing.2')}px + ${themeGet('spacing.9')}px);
`;

const defaultOption = (option_value, options_list) => (
  options_list.map((option) => {
    if (option.value === option_value) { return option; }
  })
);

export default compose(
  withState('profileData', 'setProfileData', {}),
  withState('profileIsLoaded', 'setProfileIsLoaded', false),
  withHandlers({
    handleProfileImgChange: ({ profileData, setProfileData }) => event => {
      profileData.image = event.target.files[0];
      profileData.image_url = URL.createObjectURL(event.target.files[0]);

      setProfileData(profileData);
    },
    handleSubmit: ({ profileData }) => () => {
      const formData = new FormData();

      for (const field in profileData) {
        if (field !== 'image' && typeof profileData[field] === 'object') {
          formData.append(field, JSON.stringify(profileData[field]));
        } else {
          formData.append(field, profileData[field]);
        }
      }

      updateProfile(formData).then(() => window.location.reload());
    },
    onInputChange: ({ profileData, setProfileData }) => field => event => {
      profileData[field] = event.target.value;

      setProfileData(profileData);
    },
    onDiscourseAttributesChange: ({ profileData, setProfileData }) => field => event => {
      profileData.discourse_contact_attributes[field] = event.target.value;

      setProfileData(profileData);
    },
    onLanguageChange: ({ profileData, setProfileData }) => event => {
      profileData.lang = event.value;

      setProfileData(profileData);
    },
    onTimeZoneChange: ({ profileData, setProfileData }) => event => {
      profileData.preference_attributes.time_zone = event.value;

      setProfileData(profileData);
    },
  }),
  lifecycle({
    componentDidMount() {
      editProfile().then(data => {
        this.props.setProfileData(data);
        this.props.setProfileIsLoaded(true);
      });
    }
  }),
  withNamespaces()
)(({
  onModalClose,
  onInputChange,
  handleProfileImgChange,
  profileData,
  handleSubmit,
  onDiscourseAttributesChange,
  profileIsLoaded,
  onLanguageChange,
  onTimeZoneChange,
  t
}) => (
  <Modal isOpen={true} size="medium" verticalAlign="center" contentLabel={'title'}>
    <ModalArea>
      <ModalHeader closeModal={onModalClose}><ModalTitle>{t('Edit Profile')}</ModalTitle></ModalHeader>
    </ModalArea>
    {!profileIsLoaded
      ? <Preloader />
      : <React.Fragment>
        <Flex py={5} px={6} flexDirection={'column'} justifyContent={'space-around'}>
          <Avatar url={profileData.image_url} size={'x-large'} />
          <ProfileFileInput type="file" id="file" name="file" onChange={handleProfileImgChange}/>
        </Flex>
        <Flex className="c-edit-profile-fields" px={5} pb={4} flexDirection="row" justifyContent="start" flexWrap="wrap">
          <InputWrapper className="c-name">
            <Input onChange={onInputChange('name')} id={'name'} label={t('Name')} value={profileData.name} required={true} />
          </InputWrapper>
          <InputWrapper className="c-title">
            <Input onChange={onInputChange('title')} id={'title'} label={t('Job Title')} value={profileData.title} />
          </InputWrapper>
          <TypeaheadInputWrapper className="c-language">
            <TypeAhead
              id={'language'}
              label={t('Language')}
              placeholder={t('Select language')}
              options={profileData.supported_languages}
              defaultValue={defaultOption(profileData.lang, profileData.supported_languages)}
              onChange={onLanguageChange}
              isClearable={false} />
          </TypeaheadInputWrapper>
          <InputWrapper className="c-company">
            <Input onChange={onInputChange('company')} id={'company'} label={t('Company')} value={profileData.company} />
          </InputWrapper>
          <TypeaheadInputWrapper className="c-time-zone">
            <TypeAhead
              id={'time-zone'}
              label={t('Time Zone')}
              options={profileData.time_zone_list}
              defaultValue={defaultOption(profileData.preference_attributes.time_zone, profileData.time_zone_list)}
              onChange={onTimeZoneChange}
              isClearable={false} />
          </TypeaheadInputWrapper>
          <InputWrapper className="c-username">
            <Input onChange={onDiscourseAttributesChange('username')} id={'username'}  label={t('Username')} value={profileData.discourse_contact_attributes.username} />
          </InputWrapper>
        </Flex>
        <BottomLinksContainer px={6} pb={4} flexDirection={'column'} justifyContent={'space-around'}>
          <p>
            {t(`If you would like to request an export, change, or deletion of your
              data, contact`)} <a href="https://support.influitive.com/contact" target="_blank" title="Influitive Support">{t('Support')}</a>
          </p>
        </BottomLinksContainer>
        <Flex px={4} py={3} justifyContent={'flex-end'}>
          <Button onClick={handleSubmit}>{t('Save Profile')}</Button>
        </Flex>
      </React.Fragment>
    }
  </Modal>
));
